/**
 * SiteChecklistCard — Live-Website-Checkliste mit Ampelstatus pro Firma:
 * prueft Script, Pixel, Formular und Banner direkt gegen die echte Website.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListChecks, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOT = {
  green: "bg-emerald-500",
  yellow: "bg-amber-400",
  red: "bg-red-500",
};
const OVERALL = {
  green: { label: "Alles läuft", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  yellow: { label: "Teilweise eingerichtet", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  red: { label: "Setup unvollständig", cls: "bg-red-100 text-red-700 border-red-200" },
};

export const SiteChecklistCard = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/webtrack/${companyId}/site-check`, { withCredentials: true });
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { setData(null); if (companyId) run(); }, [companyId, run]);

  return (
    <Card data-testid="site-checklist-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <ListChecks className="w-4 h-4" /> Website-Checkliste
          {data && <Badge variant="outline" className={`text-[10px] ${OVERALL[data.overall].cls}`} data-testid="site-checklist-overall">{OVERALL[data.overall].label}</Badge>}
          <div className="flex-1" />
          {data?.checked_at && (
            <span className="text-[10px] font-normal text-slate-400">
              geprüft {new Date(data.checked_at).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={run} disabled={loading} data-testid="site-checklist-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Live prüfen
          </Button>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Prüft die echte Website live: Ist das Script eingebaut? Laufen die Pixel? Kommen Formular-Leads an?
        </p>
      </CardHeader>
      <CardContent>
        {loading && !data && <div className="py-6 flex items-center justify-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Website wird geprüft …</div>}
        {!loading && !data && <p className="text-sm text-slate-400">Prüfung fehlgeschlagen — erneut versuchen.</p>}
        {data && (
          <div className="space-y-1.5">
            {data.checks.map((ch) => (
              <div key={ch.id} className="flex items-start gap-2.5 py-1" data-testid={`site-check-${ch.id}`}>
                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${DOT[ch.status]}`} title={ch.status} />
                <div className="min-w-0">
                  <p className="text-sm leading-snug">{ch.title}</p>
                  {ch.hint && <p className="text-xs text-slate-500 mt-0.5">{ch.hint}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteChecklistCard;
